import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import IconButton from "@mui/material/IconButton";
import {
  Box,
  Button,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import { api } from "../../api/api";
import { signup_url } from "../../api/urls";
import { ChangePassword_profile_page } from "../../api/urls";
import { toast } from "react-toastify";
import ProfileImage from "./ProfileImage";
import FlowOfProfile from "./Flow_of_profile";
import { Eye, EyeOff, PencilIcon, XIcon } from "lucide-react";
import Loading from "../../components/Loading";
import useFetch from "../../hooks/useFetch";
import { useSelector } from "react-redux";

const ProfilePage = () => {
  const {
    register: registerPersonal,
    handleSubmit: handleSubmitPersonal,
    reset: resetPersonal,
    control: controlPersonal,
    // watch: watchPersonal,
    formState: { errors: personalErrors },
  } = useForm({
    defaultValues: {
      full_name: "",
      middle_name: "",
      last_name: "",
      mobile_number: "",
      email: "",
      birthday: "",
      gender: "",
      company_name: "",
      gstin_number: "",
      pan_number: "",
    },
  });

  // Password Form
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPassword,
    // control: controlPassword,
    watch: watchPassword,
    getValues,
    formState: { errors: passwordErrors },
  } = useForm({
    defaultValues: {
      old_password: "",
      new_password: "",
      confirm_password: "",
    },
  });
  const mobileCode = 91;
  const [isPersonalEditable, setIsPersonalEditable] = useState(false);
  const [isPasswordEditable, setIsPasswordEditable] = useState(false);
  const [user, setUser] = useState(null);
  const [originalData, setOriginalData] = useState({});
  const [showMobileEditPopup, setShowMobileEditPopup] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // for Old Password
  const [showNewPassword, setShowNewPassword] = useState(false); // for New Password
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // for Confirm Password
  const [alternateRegex] = useState(/^[6-9]\d{9}$/);
  const convertDOBToInputFormat = (dob) => {
    if (!dob) return "";
    const [day, month, year] = dob.split("-");
    return `${year}-${month}-${day}`;
  };

  const { accessToken } = useSelector((state) => state.auth);

  const { data: userData, refetch } = useFetch(accessToken && signup_url);

  // useEffect(() => {
  //   const fetchUserData = async () => {
  //     try {
  //       if (userData) {
  //         setUser(userData);
  //         const updatedData = {
  //           ...userData,
  //           birthday: convertDOBToInputFormat(userData.birthday),
  //         };

  //         //  Load personal form with fixed DOB format
  //         resetPersonal(updatedData);
  //         setOriginalData(updatedData);

  //         //  resetPersonal(response.data);
  //         // setOriginalData({ ...response.data });
  //       }
  //     } catch (error) {
  //       console.error("Error fetching user data:", error);
  //     }
  //   };
  //   fetchUserData();
  // }, [resetPersonal, userData]);
  useEffect(() => {
    const fetchUserData = async () => {
      if (userData) {
        setUser(userData);
        const updatedData = {
          ...userData,
          birthday: convertDOBToInputFormat(userData.birthday),
        };

        // map backend keys to frontend form fields
        const mappedData = {
          ...updatedData,
          alternate_mobile: userData.alternate_number || "",
          alternate_mobile_code: userData.alternate_mobile_code || "",
        };

        resetPersonal(mappedData);
        setOriginalData(mappedData);
      }
    };
    fetchUserData();
  }, [resetPersonal, userData]);
  const [personLoading, setPersonLoading] = useState(false);
  const handlePersonalSubmit = async (data) => {
    const changedData = {};
    if (data.alternate_mobile !== originalData.alternate_number) {
      changedData.alternate_number = data.alternate_mobile;
    }
    if (data.alternate_mobile_code !== originalData.alternate_mobile_code) {
      changedData.alternate_mobile_code = data.alternate_mobile_code;
    }

    for (const key in originalData) {
      if (data[key] !== originalData[key]) {
        changedData[key] = data[key];
      }
    }
    if (changedData.birthday) {
      const [year, month, day] = changedData.birthday.split("-");
      changedData.birthday = `${day}-${month}-${year}`;
    }
    if (changedData.mobile_number) changedData.country_code = mobileCode;
    if (Object.keys(changedData).length === 0) {
      toast.info("No changes made.");
      return;
    }
    try {
      setPersonLoading(true);
      await api.patch(signup_url + `${user.id}/`, changedData);
      // console.log("Payload to backend:", changedData);

      toast.success("Personal info updated successfully");
      refetch();
      setIsPersonalEditable(false);
    } catch (error) {
      toast.error(" Failed to update personal info");
    } finally {
      setPersonLoading(false);
    }
  };
  const [passLoading, setPassLoading] = useState(false);
  const handlePasswordSubmit = async (data) => {
    try {
      setPassLoading(true);
      const payload = {
        old_password: data.old_password,
        new_password: data.new_password,
        confirm_password: data.confirm_password,
      };
      await api.patch(ChangePassword_profile_page, payload);
      toast.success("Password updated successfully");
      resetPassword("");
      setIsPasswordEditable(false);
    } catch (error) {
      const errMsg = error.response.data.data;
      toast.error(errMsg);
    } finally {
      setPassLoading(false);
    }
  };

  // if (!user) return <Typography>Loading...</Typography>;
  if (!user) return <Loading />;

  return (
    <>
      <div className="max-w-[1300px] mx-auto my-4 p-3">
        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: 500,
            fontSize: "28px",
          }}
          variant="h4"
          mb={3}
        >
          Profile
        </Typography>
        <Paper
          elevation={0}
          sx={{ p: 2, mb: 3, borderRadius: 3, border: "1px solid #ccc" }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <ProfileImage
                image={user?.profile_image || ""}
                user_id={user.id}
                refetch={refetch}
              />
              <Box>
                <p className="font-medium">{user?.full_name || "Guest"}</p>
                <Box display="flex" alignItems="center" gap={1}>
                  <p className="text-sm">
                    {/* +91 {user.mobile_number} */}
                    +91 {user.mobile_number?.replace(/^91/, "")}
                  </p>
                  <div
                    onClick={() => setShowMobileEditPopup(true)}
                    className="hover:text-gray-600 cursor-pointer p-2 rounded-2xl hover:bg-gray-200"
                  >
                    <PencilIcon size={15} />
                  </div>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Personal Info Form */}
        <form onSubmit={handleSubmitPersonal(handlePersonalSubmit)}>
          <Paper
            elevation={0}
            sx={{ p: 4, mb: 4, borderRadius: 3, border: "1px solid #ccc" }}
          >
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "20px",
                  // mt:1,
                  // mb: 3,
                }}
              >
                Personal Information
              </Typography>
              {isPersonalEditable ? (
                <XIcon
                  color="#bbb"
                  className="cursor-pointer"
                  onClick={() => {
                    setIsPersonalEditable(false);
                    // setIsPersonalEditable(false);
                  }}
                />
              ) : (
                <button
                  onClick={() => {
                    setIsPersonalEditable(true);
                    // setIsPasswordEditable(false);
                  }}
                  className="flex gap-1 items-center hover:text-gray-600 text-sm transition-all"
                >
                  <PencilIcon size={14} /> Edit
                </button>
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    mb: 1,
                    color: "#000",
                  }}
                >
                  First name
                </Typography>

                <TextField
                  placeholder="First Name"
                  fullWidth
                  size="small"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: false,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-input": {
                      color: "#000",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ccc",
                      },
                      "&:hover fieldset": {
                        borderColor: "#999",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#000",
                      },
                    },
                  }}
                  {...registerPersonal("full_name", {
                    required: "Full name is required",
                    minLength: {
                      value: 2,
                      message: "Full name must be at least 2 characters long",
                    },
                    maxLength: {
                      value: 50,
                      message: "Full name cannot exceed 50 characters",
                    },
                    pattern: {
                      value: /^[A-Za-z\s]+$/,
                      message: "Only alphabets are allowed",
                    },

                    validate: (value) =>
                      !/^\d+$/.test(value) || "Enter a valid name ",
                  })}
                  error={!!personalErrors.full_name}
                  helperText={personalErrors.full_name?.message}
                  disabled={!isPersonalEditable}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    mb: 1,
                    color: "#000",
                  }}
                >
                  Middle name
                </Typography>
                {/* 
              <TextField
              placeholder="Middle name"
                size="small"
                // label="Middle Name"
                {...registerPersonal("middle_name")}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: "#000", // label when not focused
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000", // label when focused
                  },

                  "&:hover fieldset": {
                    borderColor: "#999",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#000",
                  },
                }}
                fullWidth
                disabled={!isPersonalEditable}
                error={!!personalErrors.middle_name}
                helperText={personalErrors.middle_name?.message}
              /> */}
                <TextField
                  placeholder="Middle name"
                  size="small"
                  fullWidth
                  disabled={!isPersonalEditable}
                  {...registerPersonal("middle_name", {
                    pattern: {
                      value: /^[A-Za-z\s]*$/,
                      message: "Only alphabets are allowed",
                    },
                    validate: (value) =>
                      value.trim() === "" ||
                      /^[A-Za-z\s]+$/.test(value) ||
                      "Enter a valid middle name",
                  })}
                  error={!!personalErrors.middle_name}
                  helperText={personalErrors.middle_name?.message}
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "#000",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#000",
                    },
                    "&:hover fieldset": {
                      borderColor: "#999",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    mb: 1,
                    color: "#000",
                  }}
                >
                  Last Name
                </Typography>
                {/* <TextField
               placeholder="Surname"
                size="small"
                // label="Last Name"
                fullWidth
                disabled={!isPersonalEditable}
                {...registerPersonal("last_name")}
              /> */}
                <TextField
                  placeholder="Last Name"
                  size="small"
                  fullWidth
                  disabled={!isPersonalEditable}
                  {...registerPersonal("last_name", {
                    required: "Last Name is required",
                    pattern: {
                      value: /^[A-Za-z\s]+$/,
                      message: "Only alphabets are allowed",
                    },
                    validate: (value) =>
                      !/^\s+$/.test(value) || "Enter a valid surname",
                  })}
                  error={!!personalErrors.last_name}
                  helperText={personalErrors.last_name?.message}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    mb: 1,
                    color: "#000",
                  }}
                >
                  Email
                </Typography>
                <TextField
                  placeholder="Email"
                  size="small"
                  // label="Email"
                  type="email"
                  sx={{ backgroundColor: "#f0f0f0" }}
                  fullWidth
                  disabled={!isPersonalEditable}
                  {...registerPersonal("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Invalid email address",
                    },
                  })}
                  error={!!personalErrors.email}
                  helperText={personalErrors.email?.message}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    mb: 1,
                    color: "#000",
                  }}
                >
                  DOB
                </Typography>
                <TextField
                  // label="DOB"
                  size="small"
                  type="date"
                  sx={{ backgroundColor: "#f0f0f0" }}
                  fullWidth
                  disabled={!isPersonalEditable}
                  {...registerPersonal("birthday")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              {/* alternate number starts here */}
              {/* <Grid item xs={12} sm={4}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  mb: 1,
                  color: "#000",
                }}
              >
               Alternate Number
              </Typography>
              <TextField
                // label="DOB"
                size="small"
                type="date"
                sx={{ backgroundColor: "#f0f0f0" }}
                fullWidth
                disabled={!isPersonalEditable}
                {...registerPersonal("birthday")}
                InputLabelProps={{
                  shrink: true, 
                }}
              />
            </Grid> */}
              <Grid item xs={12} sm={4}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    mb: 1,
                    color: "#000",
                  }}
                >
                  Alternate Number
                </Typography>
                <Controller
                  name="alternate_mobile"
                  // control={control}
                  control={controlPersonal}
                  rules={{
                    validate: (value) => {
                      if (!value) return true; // optional field
                      if (!alternateRegex.test(value))
                        return "Invalid phone number";
                      if (value === getValues("primary_mobile")) {
                        return "Alternate number must be different";
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      type="tel"
                      placeholder="Alternate number"
                      sx={{ backgroundColor: "#f0f0f0" }}
                      disabled={!isPersonalEditable}
                      value={field.value?.replace(/^0+/, "") || ""}
                      onChange={(e) => {
                        const value = e.target.value.replace(/^0+/, "");
                        field.onChange(value);
                      }}
                      slotProps={{
                        input: {
                          inputMode: "numeric",
                          onInput: (e) => {
                            e.target.value = e.target.value.replace(
                              /[^0-9]/g,
                              "",
                            );
                            field.onChange(e.target.value);
                          },
                        },
                      }}
                      // error={!!errors.alternate_mobile}
                      // helperText={errors.alternate_mobile?.message}
                      error={!!personalErrors.alternate_mobile}
                      helperText={personalErrors.alternate_number?.message}
                    />
                  )}
                />
              </Grid>

              {/* ends here*/}
              <Grid item xs={12}>
                <Controller
                  name="gender"
                  control={controlPersonal}
                  render={({ field }) => (
                    <RadioGroup row {...field}>
                      <FormControlLabel
                        value="Male"
                        control={<Radio />}
                        label="Male"
                        disabled={!isPersonalEditable}
                      />
                      <FormControlLabel
                        value="Female"
                        control={<Radio />}
                        label="Female"
                        disabled={!isPersonalEditable}
                      />
                    </RadioGroup>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 500,
                    fontSize: "20px",
                  }}
                  mt={6}
                  mb={1}
                >
                  GSTIN Details (Optional)
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    mb: 1,
                    color: "#000",
                  }}
                >
                  Company name
                </Typography>
                <TextField
                  // label="Company Name"
                  fullWidth
                  placeholder="Company Name"
                  size="small"
                  disabled={!isPersonalEditable}
                  {...registerPersonal("company_name")}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    mb: 1,
                    color: "#000",
                  }}
                >
                  GST No
                </Typography>
                <TextField
                  placeholder="GST No."
                  // label="GST No"
                  fullWidth
                  size="small"
                  disabled={!isPersonalEditable}
                  {...registerPersonal("gstin_number")}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    mb: 1,
                    color: "#000",
                  }}
                >
                  PAN No
                </Typography>
                <TextField
                  // label="PAN No"
                  fullWidth
                  placeholder="PAN No."
                  size="small"
                  disabled={!isPersonalEditable}
                  {...registerPersonal("pan_number")}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Button
                type="submit"
                variant="contained"
                disabled={!isPersonalEditable}
                sx={{
                  color: "#fff",
                  border: "1px solid #ccc",
                  display: "flex",
                  justifyContent: "center",
                  textAlign: "center",
                  fontSize: "14px",
                  // padding: "13px 10px 10px 10px",
                  padding: "13px 32px 10px 32px",
                  height: "32px",
                  minHeight: "unset",

                  // textTransform: "none",
                  backgroundColor: "#000000",
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "none",
                    backgroundColor: "#111",
                  },
                  "&:focus": {
                    boxShadow: "none",
                  },
                }}
                loading={personLoading}
              >
                Save{" "}
              </Button>
            </Box>
          </Paper>
        </form>

        {/* Password Form */}
        <form onSubmit={handleSubmitPassword(handlePasswordSubmit)}>
          <Paper
            elevation={0}
            sx={{ p: 4, mb: 4, borderRadius: 3, border: "1px solid #ccc" }}
          >
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "20px",
                }}
              >
                Change Password
              </Typography>

              {isPasswordEditable ? (
                <XIcon
                  className="cursor-pointer text-gray-400 hover:text-black"
                  onClick={() => {
                    setIsPasswordEditable(false);
                    // setIsPersonalEditable(false);
                  }}
                />
              ) : (
                <button
                  onClick={() => {
                    setIsPasswordEditable(true);
                  }}
                  className="flex gap-1 items-center hover:text-gray-600 text-sm transition-all"
                >
                  <PencilIcon size={14} /> Edit
                </button>
              )}
            </Box>

            <Grid
              container
              spacing={2}
              sx={{ m: 0, width: "100%" }}
              overflow="hidden"
            >
              <Grid item xs={12} sm={4}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    mb: 1,
                    color: "#000",
                  }}
                >
                  Old password
                </Typography>
                <TextField
                  // sx={{ backgroundColor: "#f0f0f0" }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                  size="small"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  placeholder="********"
                  disabled={!isPasswordEditable}
                  InputLabelProps={{ shrink: true }}
                  {...registerPassword("old_password", {
                    required: "Old password is required",
                  })}
                  error={!!passwordErrors.old_password}
                  helperText={passwordErrors.old_password?.message}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        // onClick={() => setShowPassword(!showPassword)}
                        onClick={() =>
                          isPasswordEditable && setShowPassword(!showPassword)
                        }
                        edge="end"
                        size="small"
                      >
                        {showPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </IconButton>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    mb: 1,
                    color: "#000",
                  }}
                >
                  New Password
                </Typography>

                <TextField
                  // sx={{ background: "#f0f0f0" }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                  size="small"
                  type={showNewPassword ? "text" : "password"}
                  fullWidth
                  placeholder="********"
                  disabled={!isPasswordEditable}
                  InputLabelProps={{ shrink: true }}
                  {...registerPassword("new_password", {
                    required: "New password is required",
                    pattern: {
                      value:
                        /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/,
                      message:
                        "Include uppercase, number, special char, 6+ chars",
                    },
                  })}
                  error={!!passwordErrors.new_password}
                  helperText={passwordErrors.new_password?.message}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        // onClick={() => setShowNewPassword(!showNewPassword)}
                        onClick={() =>
                          isPasswordEditable &&
                          setShowNewPassword(!showNewPassword)
                        }
                        edge="end"
                        size="small"
                      >
                        {showNewPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </IconButton>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    mb: 1,
                    color: "#000",
                  }}
                >
                  Confirm password
                </Typography>

                <TextField
                  size="small"
                  // sx={{ background: "#f0f0f0" }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                  type={showConfirmPassword ? "text" : "password"}
                  fullWidth
                  placeholder="********"
                  disabled={!isPasswordEditable}
                  InputLabelProps={{ shrink: true }}
                  {...registerPassword("confirm_password", {
                    required: "Confirm  password is required",
                    validate: (value) =>
                      value === watchPassword("new_password") ||
                      "Passwords do not match",
                  })}
                  error={!!passwordErrors.confirm_password}
                  helperText={passwordErrors.confirm_password?.message}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        // onClick={() =>
                        //   setShowConfirmPassword(!showConfirmPassword)
                        // }
                        onClick={() =>
                          isPasswordEditable &&
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Button
                type="submit"
                variant="contained"
                disabled={!isPasswordEditable}
                sx={{
                  color: "#fff",

                  boxShadow: "none",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                  padding: "13px 32px 10px 32px",
                  height: "32px",
                  minHeight: "unset",
                  backgroundColor: "#000000",
                  "&:hover": {
                    boxShadow: "none",
                    backgroundColor: "#111",
                  },
                  "&:focus": {
                    boxShadow: "none",
                  },
                }}
                loading={passLoading}
              >
                Save
              </Button>
            </Box>
          </Paper>
        </form>
      </div>

      <FlowOfProfile
        showMobileEditPopup={showMobileEditPopup}
        onClose={() => setShowMobileEditPopup(false)}
      />
    </>
  );
};

export default ProfilePage;
